import {useMemo} from 'react'
import {MonoText} from '@/components/shared/MonoText'
import {useQuery} from '@tanstack/react-query'
import {Plus, Check, X} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {AccessProfileControllerProfilesEndpoint} from '@/types/endpoint-contracts'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {AccessProfileStatus} from '@/types/access-profile'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {permissions} from '@/lib/permissions'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'

interface AccessProfilesProps {
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

export function AccessProfiles({embedded, ownedByMe, emptyStateTitle}: AccessProfilesProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.accessProfiles.pageTitle)
    const navigate = useNavigate()
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: AccessProfileControllerProfilesEndpoint,
        sortable: true,
        defaultSortBy: AccessProfileControllerProfilesEndpoint.sort.CODE,
        defaultSortDir: SortDir.ASC,
    })

    const {listParams, canCreate, title, emptyMessage} = useEmbeddedListProps({
        contract: AccessProfileControllerProfilesEndpoint,
        ownedByMe,
        baseQueryString: queryString,
        emptyStateTitle,
        defaultTitle: i18n.accessProfiles.allProfiles,
        createPermission: permissions.accessProfiles.create,
    })
    const {environmentTypes: environments, accessProfileStatuses: statuses} = useAppConfig()

    const {data: profilesData} = useQuery({
        queryKey: ['access-profiles', queryString, page, pageSize, ownedByMe],
        queryFn: () => services.accessProfiles.list(listParams),
    })

    const profiles = profilesData?.content || []
    const totalItems = profilesData?.totalElements || 0
    const totalPages = profilesData?.totalPages || 0

    const hasFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    // Get status color
    const getStatusColor = (profileStatus: AccessProfileStatus) => {
        switch (profileStatus) {
            case AccessProfileStatus.ACTIVE:
                return BadgeVariant.SUCCESS
            case AccessProfileStatus.DEPRECATED:
                return BadgeVariant.SECONDARY
            default:
                return BadgeVariant.OUTLINE
        }
    }

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.accessProfile}
            title={title}
            count={totalItems}
            description={`${i18n.accessProfiles.profilesCount({count: totalItems})}${hasFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="access-profiles-list"
            embedded={embedded}
            actions={canCreate ? (
                <Button data-testid="btn-create-profile" onClick={() => navigate(routes.accessGovernance.profiles.create())}>
                    <Icon icon={Plus} size="md" className="mr-2"/>
                    {i18n.accessProfiles.createProfile}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={AccessProfileControllerProfilesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.accessProfiles.searchByName}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-code">{i18n.accessProfiles.code}</TableHead>
                        <TableHead data-testid="col-resource">{i18n.accessProfiles.resource}</TableHead>
                        <TableHead data-testid="col-environment">{i18n.accessProfiles.environment}</TableHead>
                        <TableHead data-testid="col-risk">{i18n.accessProfiles.risk}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-requestable">{i18n.accessProfiles.requestable}</TableHead>
                        <TableHead data-testid="col-auto-assignable">{i18n.accessProfiles.autoAssignable}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {profiles.map((profile) => (
                        <ClickableTableRow key={profile.id}
                                  to={routes.accessGovernance.profiles.detail(profile.id)}>
                            <TableCell className="font-medium">
                                <TextLink
                                    testId={`link-profile-detail-${profile.id}`}
                                    to={routes.accessGovernance.profiles.detail(profile.id)}
                                    stopPropagation
                                >
                                    {profile.name}
                                </TextLink>
                                {profile.description && (
                                    <MutedText size="sm" className="block">
                                        {profile.description}
                                    </MutedText>
                                )}
                            </TableCell>
                            <TableCell>
                                <MonoText size="xs" muted>
                                    {profile.code}
                                </MonoText>
                            </TableCell>
                            <TableCell>
                                <EntityLink value={profile.resource} stopPropagation/>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" >
                                    {labelOf(environments, profile.environment)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <RiskLevelBadge riskLevel={profile.riskLevel}/>
                            </TableCell>
                            <TableCell>
                                <Badge variant={getStatusColor(profile.status)}>
                                    {labelOf(statuses, profile.status)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {profile.requestable ? (
                                    <Icon icon={Check} size="md" className="text-status-success"/>
                                ) : (
                                    <Icon icon={X} size="md" muted/>
                                )}
                            </TableCell>
                            <TableCell>
                                {profile.autoAssignable ? (
                                    <Icon icon={Check} size="md" className="text-status-success"/>
                                ) : (
                                    <Icon icon={X} size="md" muted/>
                                )}
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {profiles.length === 0 && (
                        <TableEmptyState
                            colSpan={8}
                            hasFilters={hasFilters}
                            matchMessage={i18n.accessProfiles.noProfilesMatch}
                            emptyMessage={emptyMessage ?? i18n.accessProfiles.noProfilesFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
